import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const clTrack = (name, data) => {
  try {
    // @ts-ignore Shopify analytics global; custom events reach Customer Events pixels (Meta, Clarity…)
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {}
};

/**
 * Shop the look popup: pick a colour per product, add one piece or the whole look.
 * After adding, the popup closes so the cart drawer is visible.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [items]
 * @property {HTMLElement[]} [prices]
 * @property {HTMLImageElement[]} [images]
 * @property {HTMLButtonElement[]} [adds]
 * @property {HTMLButtonElement} [addAll]
 * @property {HTMLElement} [total]
 * @property {HTMLElement} [error]
 *
 * @extends Component<Refs>
 */
class ClaspLabLookShop extends Component {
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#renderTotal();
  }

  /** on:click="/closeDialog" */
  closeDialog() {
    const dialog = /** @type {any} */ (this.closest('dialog-component'));
    dialog?.closeDialog?.();
  }

  /**
   * on:change="/pick/{index}"
   * @param {string} arg
   * @param {Event} event
   */
  pick(arg, event) {
    const item = this.refs.items?.[Number(arg)];
    const select = event?.target instanceof HTMLSelectElement ? event.target : item?.querySelector('select');
    const option = select?.selectedOptions[0];
    if (!item || !option) return;
    item.dataset.variantId = option.value;
    item.dataset.price = option.dataset.price || '0';
    item.dataset.available = option.dataset.available || 'true';
    const price = this.refs.prices?.[Number(arg)];
    if (price && option.dataset.priceLabel) price.textContent = option.dataset.priceLabel;
    const img = item.querySelector('.cl-look__product-media img');
    if (img instanceof HTMLImageElement && option.dataset.image) {
      img.removeAttribute('srcset');
      img.src = option.dataset.image;
    }
    this.#renderTotal();
  }

  /** on:click="/addOne/{index}" */
  addOne(arg) {
    const item = this.refs.items?.[Number(arg)];
    if (!item) return;
    this.#add([item], this.refs.adds?.[Number(arg)]);
  }

  /** on:click="/addAll" */
  addAll() {
    const items = (this.refs.items || []).filter((i) => i.dataset.available !== 'false');
    this.#add(items, this.refs.addAll);
  }

  #renderTotal() {
    const { total } = this.refs;
    if (!total) return;
    const cents = (this.refs.items || [])
      .filter((i) => i.dataset.available !== 'false')
      .reduce((sum, i) => sum + Number(i.dataset.price || 0), 0);
    const value = cents / 100;
    total.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * @param {HTMLElement[]} rows
   * @param {HTMLButtonElement | undefined} button
   */
  async #add(rows, button) {
    if (this.#busy || !rows.length) return;
    // @ts-ignore set by snippets/cl-preorder.liquid when pre-orders are on
    const preorder = window.ClaspLabPreorder;
    const items = rows
      .map((r) => {
        const item = { id: Number(r.dataset.variantId), quantity: 1 };
        return preorder ? preorder.line(item, r.dataset.preorder === 'true') : item;
      })
      .filter((i) => i.id);
    if (!items.length) return;

    this.#busy = true;
    button?.setAttribute('aria-busy', 'true');
    if (this.refs.error) this.refs.error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cl-look-shop',
          sourceId: this.id || 'cl-look',
          itemCount: items.length,
          sections: response.sections,
          didError,
        },
      });
      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (this.refs.error) {
          this.refs.error.textContent = response.description || response.message || 'Could not add to cart.';
          this.refs.error.hidden = false;
        }
      } else {
        clTrack('look_added', { items: items.length, whole_look: button === this.refs.addAll });
        button?.setAttribute('data-added', '');
        this.closeDialog();
      }
    } catch (e) {
      console.error(e);
      deferred.reject(e);
      if (this.refs.error) {
        this.refs.error.textContent = 'Could not add to cart. Please try again.';
        this.refs.error.hidden = false;
      }
    } finally {
      this.#busy = false;
      button?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cl-look-shop')) customElements.define('cl-look-shop', ClaspLabLookShop);
